/**
 * Formats a monetary amount with the appropriate currency symbol and decimals.
 *
 * @param amount - The amount in smallest currency unit (e.g., cents for USD)
 * @param currency - The ISO currency code (e.g., 'usd', 'eur', 'jpy')
 * @returns Formatted string with currency symbol
 */

type Formatter = (amount: number) => string;

// Cache for formatters to improve performance
const _formatterCache: Record<string, Formatter> = {};

// Currencies with no decimal places
const ZERO_DECIMAL_CURRENCIES = [
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
];

// Three decimal currencies
const THREE_DECIMAL_CURRENCIES = ['bhd', 'jod', 'kwd', 'omr', 'tnd'];

/**
 * Get the number of decimal places for a currency
 */
const getCurrencyDecimalLength = (currency?: string | null): number => {
  const normalizedCurrency = currency?.toLowerCase();

  if (!normalizedCurrency) {
    return 2;
  }

  if (ZERO_DECIMAL_CURRENCIES.includes(normalizedCurrency)) {
    return 0;
  }

  if (THREE_DECIMAL_CURRENCIES.includes(normalizedCurrency)) {
    return 3;
  }

  // Default to 2 decimal places
  return 2;
};

/**
 * Create a formatter for a specific currency
 */
const createFormatter = (currency?: string | null): Formatter => {
  const upcaseCurrency = currency?.toUpperCase();
  const decimalLength = getCurrencyDecimalLength(currency);

  const formatterOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: upcaseCurrency || 'USD',
    minimumFractionDigits: decimalLength,
    maximumFractionDigits: decimalLength,
  };

  try {
    const formatter = new Intl.NumberFormat('en-US', formatterOptions);
    return (amount: number) => formatter.format(amount);
  } catch (error) {
    // Fallback for unsupported currencies
    return (amount: number) => {
      const formattedValue = amount.toFixed(decimalLength);
      return `${upcaseCurrency || 'USD'} ${formattedValue}`;
    };
  }
};

/**
 * Get a cached formatter or create a new one
 */
const getFormatter = (currency?: string | null): Formatter => {
  const cacheKey = currency?.toLowerCase() || 'usd';

  // Return cached formatter if available
  if (_formatterCache[cacheKey]) {
    return _formatterCache[cacheKey];
  }

  // Create and cache new formatter
  const formatter = createFormatter(currency);
  _formatterCache[cacheKey] = formatter;

  return formatter;
};

/**
 * Format a monetary amount
 */
const formatAmount = (amount: number, currency?: string | null): string => {
  const decimalLength = getCurrencyDecimalLength(currency);
  const formatter = getFormatter(currency);

  const roundedAmount = Math.round(amount);
  const currencyAdjustedAmount = roundedAmount / 10 ** decimalLength;

  return formatter(currencyAdjustedAmount);
};

export default formatAmount;
